import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Plus, Search, X, Trash2, History, Send,
  PauseCircle, PlayCircle, AlertCircle, Pencil, Copy, Calculator,
} from "lucide-react";

const RISK_OPTIONS = ["Conservative", "Moderate", "Aggressive", "Custom"];
const FREQ_OPTIONS = [
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
  { v: "monthly", label: "Monthly" },
];

// Per-asset-class categories per spec. "Custom" lets the admin type in a
// future / one-off classification without code changes.
const EQUITY_CATS = ["Large Cap", "Flexi Cap", "Mid Cap", "Small Cap", "International", "Custom"];
const DEBT_CATS = ["Corporate Bond", "Short Duration", "Gilt", "Custom"];
const HYBRID_CATS = ["Balanced Advantage", "Multi Asset", "Aggressive Hybrid", "Conservative Hybrid", "Equity Savings", "Arbitrage", "Custom"];
const LIQUID_CATS = ["Liquid", "Custom"];

// Categories that require explicit Weight % entry (must sum to 100%). The
// portfolio allocator then derives the rupee split from these weights at
// transaction time. Every other category (Tax, Short Term, Commodities, …)
// keeps the legacy weight-less mode where the investor decides ratios live.
const WEIGHTED_CATEGORIES = new Set(["wealth", "thematic", "foreign portfolio"]);

const emptyPortfolio = () => ({
  portfolio_name: "",
  portfolio_code: "",
  description: "",
  risk_category: "",
  status: "draft",
  remarks: "",
  show_in_provisional_buy: true,
  effective_date: "",
  equity_schemes: [],
  debt_schemes: [],
  hybrid_schemes: [],
  liquid_schemes: [],
});

const fmtPct = (n) => `${(Number(n) || 0).toFixed(2)}%`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

// ─── SchemeSearch — debounced autocomplete (Regular plan only; ETFs filtered server-side)
function SchemeSearch({ onSelect, placeholder = "Search Regular-plan scheme name / code / ISIN", dataTestId, assetClass, categoryName }) {
  const { api } = useAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q, limit: "20" });
        if (assetClass) params.set("asset_class", assetClass);
        if (categoryName) params.set("category", categoryName);
        const { data } = await api.get(`/portfolio-strategy/schemes/search?${params.toString()}`);
        setItems(data.items || []);
        setOpen(true);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api, assetClass, categoryName]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid={dataTestId}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
          {!loading && items.length === 0 && q.trim().length >= 2 && (
            <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
          )}
          {items.map((s) => (
            <button
              key={s.scheme_code || s.isin}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onSelect(s); setQ(""); setItems([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0 border-gray-100"
            >
              <div className="text-sm font-medium text-gray-800">{s.scheme_name}</div>
              <div className="text-[11px] text-gray-500">
                {s.amc_name} · {s.scheme_category} · {s.scheme_code || s.amfi_code}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SchemeTable — Equity / Debt / Hybrid / Liquid sub-table
// `hideWeight` (default true after the user dropped % entry everywhere) hides
// the Weight % column AND its 100%-sum validation. Investors decide rupee
// amounts at transaction time via Provisional Buy / Allocate. The Liquid
// table has always had hideWeight=true (it's auto-equal-split server-side).
function SchemeTable({ label, rows, setRows, categories, helpText, assetClass, categoryName, hideWeight = true }) {
  const isLiquid = label === "liquid";
  const hideWeightCol = hideWeight || isLiquid;
  const total = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.weight_pct) || 0), 0),
    [rows],
  );
  const totalOk = hideWeightCol
    ? rows.length > 0
    : (Math.abs(total - 100) < 0.01 && rows.length > 0);

  const addScheme = (s) => {
    if (rows.find((r) => r.scheme_code === s.scheme_code)) {
      toast.error("Scheme already added");
      return;
    }
    // Auto-prefill category from scheme_master.scheme_category where there's
    // a sensible match; otherwise default to first option.
    const srcCat = (s.scheme_category || "").toLowerCase();
    const matched = categories.find((c) => c !== "Custom" && srcCat.includes(c.toLowerCase()))
      || categories[0];
    setRows([...rows, {
      scheme_code: s.scheme_code || s.amfi_code,
      scheme_name: s.scheme_name,
      amc_name: s.amc_name || "",
      category: matched,
      custom_category: "",
      weight_pct: 0,
    }]);
  };
  const updateRow = (i, patch) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    setRows(next);
  };
  const removeRow = (i) => {
    const next = [...rows];
    next.splice(i, 1);
    setRows(next);
  };

  return (
    <div className="space-y-3" data-testid={`scheme-table-${label}`}>
      <SchemeSearch onSelect={addScheme} dataTestId={`scheme-search-${label}`} assetClass={assetClass} categoryName={categoryName} />
      {helpText && <p className="text-[11px] text-gray-500">{helpText}</p>}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Scheme</th>
              <th className="text-left px-3 py-2 w-40">Category</th>
              {!hideWeightCol && <th className="text-right px-3 py-2 w-32">Weight %</th>}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={hideWeightCol ? 3 : 4} className="px-3 py-6 text-center text-gray-400 text-xs">No schemes — search above to add.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.scheme_code}-${i}`} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2">
                  <div className="text-gray-800">{r.scheme_name}</div>
                  <div className="text-[10px] text-gray-500">{r.amc_name} · {r.scheme_code}</div>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={r.category || categories[0]}
                    onChange={(e) => updateRow(i, { category: e.target.value })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    data-testid={`cat-${label}-${i}`}
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {r.category === "Custom" && (
                    <input
                      type="text"
                      value={r.custom_category || ""}
                      onChange={(e) => updateRow(i, { custom_category: e.target.value })}
                      placeholder="Type custom category…"
                      className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-[11px]"
                      data-testid={`custom-cat-${label}-${i}`}
                    />
                  )}
                </td>
                {!hideWeightCol && (
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={r.weight_pct}
                      onChange={(e) => updateRow(i, { weight_pct: Number(e.target.value) || 0 })}
                      className="w-24 text-right border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid={`weight-${label}-${i}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2 text-center">
                  <button type="button" onClick={() => removeRow(i)} className="text-gray-400 hover:text-rose-600"
                    data-testid={`remove-${label}-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-200">
            <tr>
              {hideWeightCol ? (
                <td colSpan={3} className="px-3 py-2 text-xs text-gray-600 italic">
                  {rows.length} scheme{rows.length === 1 ? "" : "s"} listed. Investors decide rupee amounts at transaction time (Provisional Buy / Allocate).
                </td>
              ) : (
                <>
                  <td colSpan={2} className="px-3 py-2 text-xs text-gray-600 font-medium">Total ({label})</td>
                  <td className={`px-3 py-2 text-right font-semibold ${totalOk ? "text-emerald-600" : "text-rose-600"}`}>
                    {fmtPct(total)}
                  </td>
                  <td></td>
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
      {!totalOk && rows.length > 0 && !hideWeightCol && (
        <div className="flex items-center gap-2 text-xs text-rose-600">
          <AlertCircle className="w-3.5 h-3.5" /> {label} schemes must total 100% (currently {fmtPct(total)}).
        </div>
      )}
    </div>
  );
}

// ─── Allocation Simulator — calls POST /portfolio-strategy/{id}/allocate
function AllocationSimulator({ portfolio }) {
  const { api } = useAuth();
  const [amount, setAmount] = useState(1000000);
  const [equityPct, setEquityPct] = useState(70);
  const [debtPct, setDebtPct] = useState(30);
  const [route, setRoute] = useState("direct");
  const [stpFreq, setStpFreq] = useState("monthly");
  const [stpDur, setStpDur] = useState(12);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const totalRatio = (Number(equityPct) || 0) + (Number(debtPct) || 0);
  const ratioOk = Math.abs(totalRatio - 100) < 0.01;

  const run = async () => {
    if (!ratioOk) { toast.error("Equity % + Debt % must equal 100"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/portfolio-strategy/${portfolio.id}/allocate`, {
        investment_amount: Number(amount),
        equity_pct: Number(equityPct),
        debt_pct: Number(debtPct),
        route,
        stp_frequency: route === "liquid_stp" ? stpFreq : undefined,
        stp_duration_months: route === "liquid_stp" ? Number(stpDur) : undefined,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Allocate failed");
      setResult(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="allocation-simulator">
      <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        Preview how this model deploys at the per-client Equity:Debt ratio. For first-time investors the model can be deployed <b>directly</b> or <b>parked in Liquid + STP</b> into the equity schemes.
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Investment Amount (₹)</label>
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="sim-amount" />
        </div>
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Equity %</label>
          <input type="number" min="0" max="100" value={equityPct} onChange={(e) => setEquityPct(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="sim-equity-pct" />
        </div>
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Debt %</label>
          <input type="number" min="0" max="100" value={debtPct} onChange={(e) => setDebtPct(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="sim-debt-pct" />
        </div>
      </div>
      {!ratioOk && <div className="text-xs text-rose-600">Equity + Debt = {totalRatio}% (must equal 100).</div>}

      <div className="border-t pt-3">
        <div className="text-xs text-gray-600 font-medium mb-2">First-time investor: deployment route</div>
        <div className="flex gap-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="sim-route" checked={route === "direct"} onChange={() => setRoute("direct")} data-testid="sim-route-direct" />
            <span>Direct (deploy straight into Equity + Debt)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="sim-route" checked={route === "liquid_stp"} onChange={() => setRoute("liquid_stp")} data-testid="sim-route-liquid" />
            <span>Liquid + STP (park in Liquid → STP into Equity)</span>
          </label>
        </div>
        {route === "liquid_stp" && (
          <div className="grid grid-cols-2 gap-3 mt-2 bg-gray-50 p-3 rounded">
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">STP Frequency</label>
              <select value={stpFreq} onChange={(e) => setStpFreq(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm" data-testid="sim-stp-freq">
                {FREQ_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">STP Duration (months)</label>
              <input type="number" min="1" value={stpDur} onChange={(e) => setStpDur(Number(e.target.value))}
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm" data-testid="sim-stp-dur" />
            </div>
          </div>
        )}
      </div>

      <button onClick={run} disabled={busy || !ratioOk}
        className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="sim-run">
        <Calculator className="w-4 h-4" /> {busy ? "Calculating…" : "Run Simulation"}
      </button>

      {result && (
        <div className="border border-gray-200 rounded-md overflow-hidden mt-3" data-testid="sim-result">
          <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600 flex justify-between">
            <span>Investment {fmtINR(result.investment_amount)} · Equity pool {fmtINR(result.equity_pool)} · Debt pool {fmtINR(result.debt_pool)}</span>
            <span>Route: <b className={result.route === "liquid_stp" ? "text-amber-700" : "text-emerald-700"}>{result.route}</b></span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Asset Class</th>
                <th className="text-left px-3 py-2">Scheme</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-right px-3 py-2">Weight %</th>
                <th className="text-right px-3 py-2">Normalized %</th>
                <th className="text-right px-3 py-2">Allocation</th>
              </tr>
            </thead>
            <tbody>
              {result.splits.map((s, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-xs">{s.asset_class}</td>
                  <td className="px-3 py-2 text-xs">{s.scheme_name}<div className="text-[10px] text-gray-500">{s.amc_name} · {s.scheme_code}</div></td>
                  <td className="px-3 py-2 text-xs">{s.category}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmtPct(s.model_weight_pct)}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmtPct(s.normalized_weight_pct)}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmtINR(s.allocation_amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td colSpan={5} className="px-3 py-2 text-xs text-gray-600 font-medium">Total Allocated</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtINR(result.total_allocated)}</td>
              </tr>
            </tfoot>
          </table>
          {result.deployment?.route === "liquid_stp" && (
            <div className="bg-amber-50/60 border-t border-amber-200 px-3 py-3 text-xs">
              <div className="font-semibold text-amber-900 mb-1">STP Schedule</div>
              <div className="text-amber-800">{result.deployment.stp_frequency} · {result.deployment.stp_duration_months} months — parking {fmtINR(result.deployment.parking_amount)}</div>
              <table className="w-full text-xs mt-2">
                <thead className="text-gray-600">
                  <tr><th className="text-left">→ Target Scheme</th><th className="text-right">Weight</th><th className="text-right">Total to STP</th></tr>
                </thead>
                <tbody>
                  {result.deployment.stp_targets.map((t, i) => (
                    <tr key={i} className="border-t border-amber-200">
                      <td className="py-1">{t.scheme_name}</td>
                      <td className="text-right">{fmtPct(t.normalized_weight_pct)}</td>
                      <td className="text-right">{fmtINR(t.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Versions drawer
function VersionsDrawer({ portfolio, onClose }) {
  const { api } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/portfolio-strategy/${portfolio.id}/versions`);
        setVersions(data.items || []);
      } finally { setLoading(false); }
    })();
  }, [api, portfolio.id]);

  const renderTable = (rows, head) => (
    <div>
      <div className="font-medium text-gray-600 mb-1">{head}</div>
      <ul className="space-y-1">
        {(rows || []).map((r, i) => (
          <li key={i} className="flex justify-between"><span>{r.scheme_name}</span><span className="text-gray-500">{fmtPct(r.weight_pct)}</span></li>
        ))}
        {(!rows || rows.length === 0) && <li className="text-gray-400 italic">—</li>}
      </ul>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="versions-drawer">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500">Version history</div>
            <div className="text-lg font-semibold text-gray-800">{portfolio.portfolio_name}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {loading && <div className="text-sm text-gray-500">Loading…</div>}
          {!loading && versions.length === 0 && <div className="text-sm text-gray-500">No published versions yet.</div>}
          {versions.map((v) => (
            <div key={v.id} className="border border-gray-200 rounded-md p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-emerald-700">v{v.version_no}</div>
                <div className="text-xs text-gray-500">{fmtDate(v.changed_at)} · {v.changed_by_email}</div>
              </div>
              {v.reason && <div className="text-xs italic text-gray-600 mb-2">"{v.reason}"</div>}
              <div className="grid grid-cols-3 gap-3 text-xs">
                {renderTable(v.new_allocation?.equity_schemes, "Equity")}
                {renderTable(v.new_allocation?.debt_schemes, "Debt")}
                {renderTable(v.new_allocation?.liquid_schemes, "Liquid")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Edit dialog
function EditDialog({ portfolio, onClose, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState(portfolio || emptyPortfolio());
  const [tab, setTab] = useState("meta"); // meta | equity | debt | hybrid | liquid | sim
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  const isExisting = Boolean(portfolio?.id);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const _catKey = (form.portfolio_name || "").trim().toLowerCase();
  const showWeightCol = WEIGHTED_CATEGORIES.has(_catKey);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio-strategy/categories");
        setCategories(data.items || []);
      } catch (e) {
        console.warn("[PortfolioStrategy] load categories failed", e);
      }
    })();
  }, [api]);

  // Keep the active tab consistent with the chosen category's structure.
  // Wealth → 4 asset-class tabs; everything else → single "schemes" tab.
  useEffect(() => {
    const isWealth = (form.portfolio_name || "").trim().toLowerCase() === "wealth";
    if (isWealth && tab === "schemes") setTab("equity");
    if (!isWealth && ["equity", "debt", "hybrid", "liquid"].includes(tab)) setTab("schemes");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.portfolio_name]);

  // Auto-populate Liquid sleeve with one liquid fund per unique AMC used
  // across Equity / Debt / Hybrid. The Liquid pool is the parking vehicle
  // for the Liquid + STP route, so the parking fund must come from the
  // same AMC as the equity/debt/hybrid scheme it will STP into.
  // Triggers on every change to the upstream sleeves. Only adds; never
  // removes (user can manually delete a parking fund if undesired).
  useEffect(() => {
    const isWealth = (form.portfolio_name || "").trim().toLowerCase() === "wealth";
    if (!isWealth) return;
    const upstream = [
      ...(form.equity_schemes || []),
      ...(form.debt_schemes || []),
      ...(form.hybrid_schemes || []),
    ];
    const amcsInUse = [...new Set(upstream.map((s) => (s.amc_name || "").trim()).filter(Boolean))];
    const existingLiquidAmcs = new Set(
      (form.liquid_schemes || []).map((s) => (s.amc_name || "").trim()).filter(Boolean),
    );
    const missing = amcsInUse.filter((a) => !existingLiquidAmcs.has(a));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const additions = [];
      for (const amc of missing) {
        try {
          const params = new URLSearchParams({ asset_class: "liquid", amc_name: amc, limit: "10" });
          const { data } = await api.get(`/portfolio-strategy/schemes/search?${params.toString()}`);
          const items = data.items || [];
          // Selection priority:
          //   1. Pure "Liquid Fund" category (the canonical parking vehicle)
          //   2. Anything matching the asset_class=liquid filter
          //   AND within that set, prefer the Growth-plan variant
          const isLiquidCat = (s) => /liquid\s*fund/i.test(s.scheme_category || "");
          const isGrowth = (s) => /\bgrowth\b/i.test(s.scheme_name || "");
          const liquidCat = items.filter(isLiquidCat);
          const liquidGrowth = liquidCat.find(isGrowth);
          const pick =
            liquidGrowth ||
            liquidCat[0] ||
            items.find(isGrowth) ||
            items[0];
          if (pick) {
            additions.push({
              scheme_code: pick.scheme_code || pick.amfi_code,
              scheme_name: pick.scheme_name,
              amc_name: pick.amc_name || amc,
              category: "Liquid",
              custom_category: "",
              weight_pct: 0,
            });
          }
        } catch (e) {
          console.warn("[PortfolioStrategy] liquid parking lookup failed for AMC:", amc, e);
        }
      }
      if (cancelled || additions.length === 0) return;
      setForm((prev) => {
        // Re-check in case state changed in flight: dedupe by scheme_code AND amc_name.
        const existing = prev.liquid_schemes || [];
        const existingCodes = new Set(existing.map((s) => s.scheme_code));
        const existingAmcs = new Set(existing.map((s) => (s.amc_name || "").trim()));
        const fresh = additions.filter((a) => !existingCodes.has(a.scheme_code) && !existingAmcs.has(a.amc_name));
        if (fresh.length === 0) return prev;
        toast.success(`Auto-added ${fresh.length} parking liquid fund${fresh.length === 1 ? "" : "s"} (one per AMC)`);
        return { ...prev, liquid_schemes: [...existing, ...fresh] };
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.portfolio_name,
    JSON.stringify((form.equity_schemes || []).map((s) => s.amc_name)),
    JSON.stringify((form.debt_schemes || []).map((s) => s.amc_name)),
    JSON.stringify((form.hybrid_schemes || []).map((s) => s.amc_name)),
  ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = isExisting ? `/portfolio-strategy/${portfolio.id}` : `/portfolio-strategy`;
      const method = isExisting ? "put" : "post";
      const { data } = await api[method](url, form);
      toast.success(isExisting ? "Saved as draft" : "Portfolio created (draft)");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const tabBtn = (k, label, disabled = false) => (
    <button
      type="button"
      onClick={() => !disabled && setTab(k)}
      disabled={disabled}
      className={`px-3 py-2 text-sm border-b-2 transition-colors ${
        tab === k ? "border-emerald-600 text-emerald-700 font-medium" :
        disabled ? "border-transparent text-gray-300 cursor-not-allowed" :
        "border-transparent text-gray-500 hover:text-gray-800"
      }`}
      data-testid={`edit-tab-${k}`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="edit-dialog">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="text-lg font-semibold text-gray-800">
            {isExisting ? `Edit: ${portfolio.portfolio_name}` : "New Model Portfolio"}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 pt-3 border-b flex gap-1 overflow-x-auto">
          {tabBtn("meta", "Metadata")}
          {(() => {
            // Wealth keeps the 4 asset-class tabs. Every other category
            // uses a single "Schemes" tab (flat list filtered by that
            // category's nature — ELSS for Tax, Liquid for Short Term, etc.)
            const isWealth = (form.portfolio_name || "").trim().toLowerCase() === "wealth";
            if (isWealth) {
              return (
                <>
                  {tabBtn("equity", `Equity Schemes (${form.equity_schemes?.length || 0})`)}
                  {tabBtn("debt", `Debt Schemes (${form.debt_schemes?.length || 0})`)}
                  {tabBtn("hybrid", `Hybrid Schemes (${form.hybrid_schemes?.length || 0})`)}
                  {tabBtn("liquid", `Liquid Schemes (${form.liquid_schemes?.length || 0})`)}
                </>
              );
            }
            return tabBtn("schemes", `Schemes (${form.equity_schemes?.length || 0})`);
          })()}
          {tabBtn("sim", "Allocation Simulator", !isExisting)}
        </div>

        <div className="p-6">
          {tab === "meta" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">Portfolio Name * (category)</label>
                <select value={form.portfolio_name || ""} onChange={(e) => update("portfolio_name", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white" data-testid="form-portfolio-name">
                  <option value="">— Pick a category —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  Manage this list in <b>Settings → Portfolio Categories</b>. A scheme can belong to only ONE category at a time.
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">Portfolio Code * (unique)</label>
                <input value={form.portfolio_code} onChange={(e) => update("portfolio_code", e.target.value.toUpperCase())}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm uppercase" data-testid="form-portfolio-code" />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">Risk Category</label>
                <select value={form.risk_category || ""} onChange={(e) => update("risk_category", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="form-risk">
                  <option value="">— None —</option>
                  {RISK_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">Status</label>
                <div className="px-3 py-2 text-sm border border-gray-200 rounded bg-gray-50 text-gray-600">
                  {form.status} {isExisting && form.is_draft ? "(unpublished changes pending)" : ""}
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 font-medium mb-1 block">Effective Date *</label>
                <input
                  type="date"
                  value={form.effective_date || ""}
                  onChange={(e) => update("effective_date", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  data-testid="form-effective-date"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Schemes purchased <b>on or after</b> this date will be tagged to this portfolio. Lots predating the effective date stay in <b>Short Term</b> (legacy bucket). Leave blank to fall back to the system epoch (2018-07-11).
                </p>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 font-medium mb-1 block">Description</label>
                <textarea rows={2} value={form.description || ""} onChange={(e) => update("description", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="form-description" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 font-medium mb-1 block">Remarks (internal notes)</label>
                <textarea rows={2} value={form.remarks || ""} onChange={(e) => update("remarks", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="form-remarks" />
              </div>
              <div className="col-span-2 border border-gray-200 rounded px-3 py-2 bg-white">
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.show_in_provisional_buy !== false}
                    onChange={(e) => update("show_in_provisional_buy", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-emerald-700"
                    data-testid="form-show-in-provisional-buy"
                  />
                  <span className="text-sm text-gray-800">
                    Show in Provisional Buy dropdown
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      Uncheck for thematic / standalone models (e.g. Gold, Liquid-only, Tax) that shouldn't participate in the deficit-based additional-buy rebalance.
                    </span>
                  </span>
                </label>
              </div>
              <div className="col-span-2 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                Only Regular Plan funds appear in scheme search. Direct ETFs are excluded; FoFs are allowed.
                The Liquid Schemes table is used only when a first-time investor opts for the <b>Liquid + STP</b> route at transaction time.
              </div>
            </div>
          )}
          {tab === "equity" && (
            <SchemeTable label="equity" categories={EQUITY_CATS}
              assetClass="equity"
              rows={form.equity_schemes || []}
              setRows={(r) => update("equity_schemes", r)}
              hideWeight={!showWeightCol}
              helpText="Equity universe for this model. Hybrid / balanced funds belong in the Hybrid tab."
            />
          )}
          {tab === "debt" && (
            <SchemeTable label="debt" categories={DEBT_CATS}
              assetClass="debt"
              rows={form.debt_schemes || []}
              setRows={(r) => update("debt_schemes", r)}
              hideWeight={!showWeightCol}
              helpText="Debt universe for this model. Hybrid / balanced funds belong in the Hybrid tab."
            />
          )}
          {tab === "hybrid" && (
            <SchemeTable label="hybrid" categories={HYBRID_CATS}
              assetClass="hybrid"
              rows={form.hybrid_schemes || []}
              setRows={(r) => update("hybrid_schemes", r)}
              hideWeight={!showWeightCol}
              helpText="Hybrid funds (Balanced Advantage, Multi Asset, Arbitrage, Equity Savings, etc.)."
            />
          )}
          {tab === "liquid" && (
            <SchemeTable label="liquid" categories={LIQUID_CATS}
              assetClass="liquid"
              rows={form.liquid_schemes || []}
              setRows={(r) => update("liquid_schemes", r)}
              helpText="Parking pool used when a first-time investor chooses Liquid + STP at transaction time."
            />
          )}
          {tab === "schemes" && (
            <SchemeTable label="schemes" categories={["Custom"]}
              categoryName={form.portfolio_name}
              rows={form.equity_schemes || []}
              setRows={(r) => update("equity_schemes", r)}
              hideWeight={!showWeightCol}
              helpText={
                showWeightCol
                  ? `Pick the schemes for the "${form.portfolio_name}" model and assign a Weight % (must total 100%). The allocator derives the rupee split from these weights at transaction time.`
                  : `Pick the schemes that belong to the "${form.portfolio_name || ""}" category. Search is auto-filtered to that category's universe (e.g. Tax → ELSS, Short Term → liquid / ultra-short, Commodities → Gold / Silver, Foreign Portfolio → International, Thematic → Sector / Thematic / Focused). No weights — investors decide rupee amounts at transaction time.`
              }
            />
          )}
          {tab === "sim" && isExisting && (
            <AllocationSimulator portfolio={portfolio} />
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-60"
            data-testid="save-draft-btn">
            {saving ? "Saving…" : (isExisting ? "Save Draft" : "Create Draft")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Publish dialog
function PublishDialog({ portfolio, onClose, onPublished }) {
  const { api } = useAuth();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/portfolio-strategy/${portfolio.id}/publish`, { reason });
      toast.success(`Published v${data.version_no}`);
      onPublished(data.portfolio);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="publish-dialog">
        <div className="px-6 py-4 border-b">
          <div className="text-lg font-semibold text-gray-800">Publish new version</div>
          <div className="text-xs text-gray-500 mt-1">A snapshot of the current state will be saved to history.</div>
        </div>
        <div className="p-6 space-y-2">
          <label className="text-xs text-gray-600 font-medium block">Reason for change (recommended)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="e.g. Reweighted equity to 60% in Aug 2025 after Budget review"
            data-testid="publish-reason" />
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700">Cancel</button>
          <button onClick={handle} disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-60"
            data-testid="publish-confirm">
            {saving ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page
export default function PortfolioStrategy() {
  const { api, user } = useAuth();
  const isAuthorized = user?.role === "super_admin" || user?.role === "platform_owner";

  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [viewingVersions, setViewingVersions] = useState(null);

  const reload = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/portfolio-strategy/dashboard"),
        api.get("/portfolio-strategy/list"),
      ]);
      setSummary(s.data);
      setList(l.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => { if (isAuthorized) reload(); /* eslint-disable-next-line */ }, [isAuthorized]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!q) return true;
      return (p.portfolio_name || "").toLowerCase().includes(q)
          || (p.portfolio_code || "").toLowerCase().includes(q);
    });
  }, [list, search, statusFilter]);

  if (!isAuthorized) {
    return (
      <div className="p-8">
        <div className="max-w-md mx-auto text-center bg-amber-50 border border-amber-200 rounded-lg p-6">
          <AlertCircle className="w-8 h-8 text-amber-600 mx-auto mb-3" />
          <div className="text-base font-semibold text-amber-900 mb-1">Restricted</div>
          <div className="text-sm text-amber-700">Only super-admin users can access the Portfolio Strategy Setup module.</div>
        </div>
      </div>
    );
  }

  const statusBadge = (st, pendingPublish) => {
    if (pendingPublish) {
      return <span className="px-2 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 font-medium">Active · Pending edits</span>;
    }
    const map = {
      active: "bg-emerald-100 text-emerald-700",
      draft: "bg-amber-100 text-amber-700",
      inactive: "bg-gray-200 text-gray-700",
    };
    return <span className={`px-2 py-0.5 text-[10px] rounded font-medium uppercase ${map[st] || "bg-gray-100 text-gray-600"}`}>{st}</span>;
  };

  const handleDeactivate = async (p) => {
    if (!window.confirm(`Deactivate "${p.portfolio_name}"?`)) return;
    try { await api.post(`/portfolio-strategy/${p.id}/status`, { status: "inactive" }); toast.success("Deactivated"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const handleActivate = async (p) => {
    try { await api.post(`/portfolio-strategy/${p.id}/status`, { status: "active" }); toast.success("Activated"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete draft "${p.portfolio_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/portfolio-strategy/${p.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const handleClone = async (p) => {
    try { await api.post(`/portfolio-strategy/${p.id}/clone`); toast.success(`Cloned "${p.portfolio_name}"`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="portfolio-strategy-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutGrid className="w-6 h-6 text-emerald-700" /> Portfolio Strategy Setup
          </h1>
          <p className="text-sm text-gray-500 mt-1">Master repository of model portfolios — Equity / Debt / Liquid · version-controlled.</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/portfolio-strategy/bulk-upload/template`}
            onClick={(e) => {
              // attach token via fetch instead of bare link (FastAPI needs Bearer)
              e.preventDefault();
              (async () => {
                const tok = localStorage.getItem("token");
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio-strategy/bulk-upload/template`, { headers: { Authorization: `Bearer ${tok}` } });
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "portfolio_bulk_upload_template.xlsx";
                a.click(); URL.revokeObjectURL(url);
              })();
            }}
            className="px-3 py-2 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer"
            data-testid="download-template-btn">
            Download Template
          </a>
          <label className="px-3 py-2 text-xs border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-md cursor-pointer hover:bg-emerald-100" data-testid="bulk-upload-btn">
            Bulk Upload XLSX
            <input type="file" accept=".xlsx" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              const tok = localStorage.getItem("token");
              try {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio-strategy/bulk-upload`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${tok}` },
                  body: fd,
                });
                const json = await r.json();
                if (!r.ok) {
                  alert(`Upload failed: ${json.detail || r.statusText}`);
                } else {
                  const created = json.created || 0;
                  const rejected = json.rejected || 0;
                  let msg = `Bulk upload complete:\n  ✓ ${created} created\n  ✗ ${rejected} rejected`;
                  if (rejected > 0) {
                    msg += "\n\nRejected portfolios:\n" + (json.results || [])
                      .filter(x => x.status === "rejected")
                      .map(x => `  • ${x.portfolio_code} — ${x.error}`)
                      .join("\n");
                  }
                  alert(msg);
                  reload();
                }
              } catch (err) {
                alert(`Upload error: ${err.message}`);
              } finally {
                e.target.value = "";
              }
            }} />
          </label>
          <button onClick={() => setEditing({})}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 shadow-sm"
            data-testid="new-portfolio-btn">
            <Plus className="w-4 h-4" /> New Portfolio
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="portfolio-strategy-dashboard">
          <KPI label="Total Portfolios" value={summary.total_portfolios} />
          <KPI label="Active" value={summary.active_portfolios} color="text-emerald-700" />
          <KPI label="Drafts" value={summary.draft_portfolios} color="text-amber-700" />
          <KPI label="Inactive" value={summary.inactive_portfolios} color="text-gray-600" />
          <KPI label="Pending Publishes" value={summary.pending_drafts} color="text-rose-700" />
        </div>
      )}

      {summary?.recent_changes?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-gray-500" />
            <div className="text-sm font-semibold text-gray-800">Latest Portfolio Changes</div>
          </div>
          <ul className="text-sm space-y-1.5">
            {summary.recent_changes.map((c) => (
              <li key={`${c.portfolio_id}-${c.version_no}`} className="flex items-center justify-between border-b last:border-b-0 border-gray-100 py-1.5">
                <div>
                  <span className="font-medium text-gray-800">{c.portfolio_name}</span>
                  <span className="text-xs text-gray-500 ml-2">v{c.version_no} · {c.changed_by_email}</span>
                  {c.reason && <span className="text-xs italic text-gray-500 ml-2">"{c.reason}"</span>}
                </div>
                <div className="text-xs text-gray-400">{fmtDate(c.changed_at)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm w-72" data-testid="portfolio-search" />
        </div>
        <div className="flex gap-1 text-xs">
          {["all", "active", "draft", "inactive"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md border ${statusFilter === s ? "bg-emerald-700 text-white border-emerald-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              data-testid={`status-filter-${s}`}>{s.toUpperCase()}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Risk</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Version</th>
              <th className="text-center px-3 py-2">Eq</th>
              <th className="text-center px-3 py-2">Debt</th>
              <th className="text-center px-3 py-2">Liquid</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400 text-sm">
                {list.length === 0 ? "No portfolios yet — click 'New Portfolio' to create your first model." : "No matches for the current filter."}
              </td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`portfolio-row-${p.portfolio_code}`}>
                <td className="px-3 py-2 font-medium text-gray-800">{p.portfolio_name}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">{p.portfolio_code}</td>
                <td className="px-3 py-2 text-xs">{p.risk_category || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2 text-center">{statusBadge(p.status, p.is_draft && p.version_no >= 1)}</td>
                <td className="px-3 py-2 text-center text-xs">v{p.version_no || 0}</td>
                <td className="px-3 py-2 text-center text-xs">{(p.equity_schemes || []).length}</td>
                <td className="px-3 py-2 text-center text-xs">{(p.debt_schemes || []).length}</td>
                <td className="px-3 py-2 text-center text-xs">{(p.liquid_schemes || []).length}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <ActionBtn label="Edit" icon={Pencil} onClick={() => setEditing(p)} testid={`edit-${p.portfolio_code}`} />
                    {(p.is_draft || (p.version_no || 0) === 0) && (
                      <ActionBtn label="Publish" icon={Send} onClick={() => setPublishing(p)} color="text-emerald-700" testid={`publish-${p.portfolio_code}`} />
                    )}
                    <ActionBtn label="Clone" icon={Copy} onClick={() => handleClone(p)} testid={`clone-${p.portfolio_code}`} />
                    <ActionBtn label="History" icon={History} onClick={() => setViewingVersions(p)} testid={`history-${p.portfolio_code}`} />
                    {p.status === "active" && (
                      <ActionBtn label="Deactivate" icon={PauseCircle} onClick={() => handleDeactivate(p)} color="text-amber-700" testid={`deactivate-${p.portfolio_code}`} />
                    )}
                    {p.status === "inactive" && (
                      <ActionBtn label="Activate" icon={PlayCircle} onClick={() => handleActivate(p)} color="text-emerald-700" testid={`activate-${p.portfolio_code}`} />
                    )}
                    {(p.version_no || 0) === 0 && (
                      <ActionBtn label="Delete" icon={Trash2} onClick={() => handleDelete(p)} color="text-rose-600" testid={`delete-${p.portfolio_code}`} />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <EditDialog portfolio={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
      {publishing && (
        <PublishDialog portfolio={publishing} onClose={() => setPublishing(null)} onPublished={() => { setPublishing(null); reload(); }} />
      )}
      {viewingVersions && (
        <VersionsDrawer portfolio={viewingVersions} onClose={() => setViewingVersions(null)} />
      )}
    </div>
  );
}

const KPI = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-gray-200 rounded-md p-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value ?? 0}</div>
  </div>
);

const ActionBtn = ({ label, icon: Icon, onClick, color = "text-gray-600", testid }) => (
  <button onClick={onClick} title={label} className={`p-1.5 rounded hover:bg-gray-100 ${color}`} data-testid={testid}>
    <Icon className="w-4 h-4" />
  </button>
);
